import {
  Box,
  ButtonProps,
  Flex,
  HStack,
  Heading,
  HeadingProps,
  Icon,
  Text,
} from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';

import Button from '../../../../../../components/ui/buttons/Button';
import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { formatMoney } from '../../../../../../utils/helpers';
import { getNetCash } from '../../netCash';
import ConversationDoorway from '../conversation/ConversationDoorway';
import OfferLinkButton from './OfferLinkButton';
import { OfferStatusBadges } from './OfferStatusBadges';
import { OfferActionKind, OfferCardView } from './utils';

type OfferCardProps = {
  card: OfferCardView;
  onAction: (kind: OfferActionKind, card: OfferCardView) => void;
  showConversationEntry: boolean;
  thread?: ContactMessageEntry[];
  onOpenConversation: () => void;
  payoffAmount: number | null;
};

const OfferAmount = ({ amount, ...headingProps }: HeadingProps & { amount: number }) => (
  <Heading
    as="span"
    fontSize="36px"
    fontWeight="700"
    lineHeight="1.1"
    color="leaseEndBlue"
    {...headingProps}
  >
    {formatMoney(amount)}
  </Heading>
);

const AcceptOfferButton = (buttonProps: ButtonProps) => (
  <Button
    leftIcon={<Icon as={FaCheck} boxSize="12px" />}
    bg="leaseEndYellow"
    color="leaseEndBlue"
    borderRadius="full"
    fontSize="12.5px"
    fontWeight="800"
    letterSpacing="0.04em"
    px="20px"
    py="10px"
    h="auto"
    _hover={{ bg: 'yellowLight' }}
    {...buttonProps}
  >
    Accept
  </Button>
);

const OfferCard = ({
  card,
  onAction,
  showConversationEntry,
  thread,
  onOpenConversation,
  payoffAmount,
}: OfferCardProps) => {
  const {
    driverCounterAmount,
    respondedToCounterAmount,
    revivedRejectedCounterAmount,
    dealerLabel,
    amount,
    isTopOffer,
  } = card;
  const isWaiting = driverCounterAmount != null;
  const netCash = !isWaiting && payoffAmount != null ? getNetCash(amount, payoffAmount) : null;

  return (
    <Box
      position="relative"
      overflow="hidden"
      borderWidth="1px"
      borderColor={isTopOffer ? 'offerTopCardBorder' : 'gainsboro'}
      borderRadius="11px"
      bg={isTopOffer ? undefined : 'white'}
      bgGradient={isTopOffer ? 'linear(96deg, offerTopCardTint 0%, white 60%)' : undefined}
      px={{ base: '18px', md: '24px' }}
      py={{ base: '16px', md: '20px' }}
    >
      {isTopOffer && (
        <Box position="absolute" insetStart={0} top={0} bottom={0} w="3px" bg="leaseEndGreen" />
      )}

      <Flex wrap="wrap" gap="8px" mb="10px" align="center">
        <Text
          as="span"
          fontFamily="heading"
          fontSize={{ base: '14px', md: '15px' }}
          fontWeight="700"
          letterSpacing="-0.01em"
          textTransform="uppercase"
          color="leaseEndBlue"
          noOfLines={1}
        >
          Offer {dealerLabel}
        </Text>
        <OfferStatusBadges card={card} />
      </Flex>

      {isWaiting ? (
        <>
          <OfferAmount
            amount={amount}
            display="block"
            mb="14px"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          />
          <Flex
            align="center"
            justify="space-between"
            wrap="wrap"
            gap="10px"
            bg="iceBlue"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="oceanBoatBlueBG"
            borderRadius="8px"
            px="14px"
            py="10px"
          >
            <Text fontSize="12px" color="oceanBoatBlue" fontWeight="500">
              You countered <strong>{formatMoney(driverCounterAmount)}</strong> - Waiting on a
              response
            </Text>
            <HStack spacing={0} ml="auto">
              <OfferLinkButton onClick={() => onAction('adjust', card)}>Adjust</OfferLinkButton>
              <Text as="span" mx="8px" color="oceanBoatBlue" opacity={0.35} userSelect="none">
                |
              </Text>
              <OfferLinkButton onClick={() => onAction('withdraw', card)}>Withdraw</OfferLinkButton>
            </HStack>
          </Flex>
        </>
      ) : (
        <Flex direction={{ base: 'column', md: 'row' }} align={{ md: 'center' }} gap="14px">
          <Box>
            <OfferAmount amount={amount} display="block" />
            {netCash != null && (
              <Text mt="6px" fontSize="14px" fontWeight="500" color="grayLight">
                {netCash.label}{' '}
                <Text
                  as="span"
                  fontWeight="700"
                  color={netCash.color}
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {netCash.amount}
                </Text>
              </Text>
            )}
            {respondedToCounterAmount != null && (
              <Text mt="6px" fontSize="12px" fontWeight="500" color="oceanBoatBlue">
                Response to your <strong>{formatMoney(respondedToCounterAmount)}</strong> counter
              </Text>
            )}
            {revivedRejectedCounterAmount != null && (
              <Text mt="6px" fontSize="12px" fontWeight="500" color="oceanBoatBlue">
                The buyer rejected your <strong>{formatMoney(revivedRejectedCounterAmount)}</strong>{' '}
                counter
              </Text>
            )}
          </Box>
          <HStack spacing="14px" ml={{ md: 'auto' }}>
            <AcceptOfferButton
              flex={{ base: 1, md: 'none' }}
              onClick={() => onAction('accept', card)}
            />
            <OfferLinkButton onClick={() => onAction('counter', card)}>Counter</OfferLinkButton>
          </HStack>
        </Flex>
      )}

      {showConversationEntry && (
        <ConversationDoorway thread={thread} history={card.history} onOpen={onOpenConversation} />
      )}
    </Box>
  );
};

export default OfferCard;
